import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Space, Tabs, type TabsProps } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined, FolderAddOutlined, PlusOutlined } from '@ant-design/icons';
import { ALL_TAB_KEY, useTransportPerformanceGroupsStore } from 'shared/stores/use-transport-performance-groups';
import { AddToGroupModal } from 'components/transport-mileage/add-to-group-modal';
import { CreateGroupModal } from 'components/transport-mileage/create-group-modal';
import { buildGroupTabItems } from './utils';

export const TransportMileageTabs = () => {
  const { t } = useTranslation('transport-mileage');
  const { groups, activeTab, selectedIds, setActiveTab, removeGroup, removeFromGroup } =
    useTransportPerformanceGroupsStore();
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [isAddToGroupOpen, setIsAddToGroupOpen] = useState(false);
  const [modal, contextHolder] = Modal.useModal();

  const tabItems = buildGroupTabItems(groups, t('all'));
  const hasGroups = Object.keys(groups).length > 0;

  const confirmRemoveGroup = (name: string) => {
    modal.confirm({
      title: t('common:confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('common:delete_confirm_text'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      okType: 'danger',
      onOk: () => removeGroup(name),
    });
  };

  const confirmRemoveFromGroup = () => {
    modal.confirm({
      title: t('common:confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('remove_from_group_confirm'),
      okText: t('remove_from_group'),
      okType: 'danger',
      cancelText: t('common:cancel'),
      onOk: () => removeFromGroup(activeTab, selectedIds),
    });
  };

  const onTabEdit: NonNullable<TabsProps['onEdit']> = (targetKey, action) => {
    if (action === 'remove' && typeof targetKey === 'string') {
      confirmRemoveGroup(targetKey);
    }
  };

  const tabBarExtraContent = selectedIds.length > 0 && (
    <Space>
      {activeTab !== ALL_TAB_KEY && (
        <Button icon={<DeleteOutlined />} danger onClick={confirmRemoveFromGroup}>
          {t('remove_from_group')}
        </Button>
      )}
      {hasGroups && (
        <Button icon={<FolderAddOutlined />} onClick={() => setIsAddToGroupOpen(true)}>
          {t('add_to_group')}
        </Button>
      )}
      <Button type='primary' icon={<PlusOutlined />} onClick={() => setIsCreateGroupOpen(true)}>
        {t('create_group')}
      </Button>
    </Space>
  );

  return (
    <>
      <Tabs
        type='editable-card'
        activeKey={activeTab}
        items={tabItems}
        tabBarExtraContent={tabBarExtraContent}
        hideAdd
        onChange={setActiveTab}
        onEdit={onTabEdit}
      />
      <CreateGroupModal isOpen={isCreateGroupOpen} onClose={() => setIsCreateGroupOpen(false)} />
      <AddToGroupModal isOpen={isAddToGroupOpen} onClose={() => setIsAddToGroupOpen(false)} />
      {contextHolder}
    </>
  );
};
